// EosCalculator.ts
// Note: keep this file free of React/UI. Pure calculations only.

type TForm = Partial<{
  temperature?: number;
  pregnancyLengthWeeks?: number;
  pregnancyLengthDays?: number;
  rom?: number;
  gbs?: "positive" | "negative" | "unknown";
  antibioticTreatment?: "none" | "broad-4" | "broad-2" | "GBS-2";
}>;

function computeInterceptBeta(intercept: number) {
  if (intercept == 0.3) return 40.0528;
  if (intercept == 0.4) return 40.3415;
  if (intercept == 0.5) return 40.5656;
  if (intercept == 0.6) return 40.7489;
  return 0;
}

function computeTemprature(temprature: number) {
  return temprature * (9 / 5) + 32; // convert to C from F
}

function computePregnancyLength(pregnancyLengthDays: number, pregnancyLengthWeeks: number) {
  if (pregnancyLengthDays) return pregnancyLengthWeeks + pregnancyLengthDays / 7;
  return pregnancyLengthWeeks;
}

function computeROM(rom: number) {
  return Math.pow(Number(rom) + 0.05, 0.2);
}

function computeApproptx1(antibioticTreatment: TForm["antibioticTreatment"]) {
  if (antibioticTreatment === "GBS-2" || antibioticTreatment === "broad-2") return 1;
  return 0;
}

function computeApproptx2(antibioticTreatment: TForm["antibioticTreatment"]) {
  if (antibioticTreatment === "broad-4") return 1;
  return 0;
}

function computeJ_gbscarPlus(gbs: TForm["gbs"]) {
  if (gbs === "positive") return 1;
  return 0;
}

function computeJ_gbscarU(gbs: TForm["gbs"]) {
  if (gbs === "unknown") return 1;
  return 0;
}

function calcEosPerClinicalCondition(eos: number) {
  const wellAppearingLiklyhood = 0.41;
  const equivocalLiklyhood = 5.0;
  const clinicalIllnessLiklyhood = 21.2;

  const eosOdds = eos / (1 - eos);
  const wellAppearingOdds = wellAppearingLiklyhood * eosOdds;
  const equivocalOdds = equivocalLiklyhood * eosOdds;
  const clinicalIllnessOdds = clinicalIllnessLiklyhood * eosOdds;

  const wellAppearingP = wellAppearingOdds / (1 + wellAppearingOdds);
  const equivocalP = equivocalOdds / (1 + equivocalOdds);
  const clinicalIllnessP = clinicalIllnessOdds / (1 + clinicalIllnessOdds);

  const eosPerClinicalCondition: any = {};
  eosPerClinicalCondition["Well Appearing"] = (wellAppearingP * 1000).toFixed(2);
  eosPerClinicalCondition["Equivocal"] = (equivocalP * 1000).toFixed(2);
  eosPerClinicalCondition["Clinical Illness"] = (clinicalIllnessP * 1000).toFixed(2);

  return eosPerClinicalCondition;
}

export function computeEOS(
  intercept: number,
  {
    temperature,
    pregnancyLengthDays,
    pregnancyLengthWeeks,
    gbs,
    rom,
    antibioticTreatment,
  }: Required<TForm>
) {
  const betas =
    computeInterceptBeta(intercept) +
    0.868 * computeTemprature(temperature) -
    6.9325 * computePregnancyLength(pregnancyLengthDays, pregnancyLengthWeeks) +
    0.0877 * Math.pow(computePregnancyLength(pregnancyLengthDays, pregnancyLengthWeeks), 2) +
    1.2256 * computeROM(rom) -
    1.0488 * computeApproptx1(antibioticTreatment) -
    1.1861 * computeApproptx2(antibioticTreatment) +
    0.5771 * computeJ_gbscarPlus(gbs) +
    0.0427 * computeJ_gbscarU(gbs);

  const eos = 1 / (1 + Math.E ** -betas);
  const eosString = (eos * 1000).toFixed(2);
  const eosPerClinicalCondition = calcEosPerClinicalCondition(eos);

  return { eos, eosString, eosPerClinicalCondition };
}